export const INITIAL_COLUMN_WIDTH = 150;
export const MINIMUM_COLUMN_WIDTH = 100;

export const fitColumnWidths = ({
  addedWidthIdx,
  containerWidth,
  columnIdxMap,
  startWidthIdx,
  widths,
}: {
  addedWidthIdx?: Set<number>;
  containerWidth: number;
  columnIdxMap: number[];
  startWidthIdx: number;
  widths: number[];
}) => {
  const nextWidths = [...widths];

  let totalWidth = columnIdxMap.reduce(
    (acc, widthIdx) => acc + nextWidths[widthIdx],
    0
  );

  if (totalWidth < containerWidth) {
    // grow columns equally to fill container width
    const diff = (containerWidth - totalWidth) / columnIdxMap.length;
    columnIdxMap.forEach((widthIdx) => {
      nextWidths[widthIdx] = Math.max(
        MINIMUM_COLUMN_WIDTH,
        nextWidths[widthIdx] + diff
      );
    });
    return nextWidths;
  }

  if (totalWidth > containerWidth && addedWidthIdx?.size) {
    // shrink other columns equally to fill container width
    const diff =
      (containerWidth - totalWidth) /
      (columnIdxMap.length - addedWidthIdx.size);
    columnIdxMap.forEach((widthIdx) => {
      if (!addedWidthIdx.has(widthIdx)) {
        nextWidths[widthIdx] = Math.max(
          MINIMUM_COLUMN_WIDTH,
          nextWidths[widthIdx] + diff
        );
      }
    });
    return nextWidths;
  }

  let shrinkWidthIdx = startWidthIdx;
  const shrinkColumn = () => {
    if (nextWidths[shrinkWidthIdx] === undefined) {
      return;
    }

    if (!columnIdxMap.includes(shrinkWidthIdx)) {
      return;
    }

    nextWidths[shrinkWidthIdx] = Math.max(
      MINIMUM_COLUMN_WIDTH,
      nextWidths[shrinkWidthIdx] - totalWidth + containerWidth
    );
    totalWidth = columnIdxMap.reduce(
      (acc, widthIdx) => acc + nextWidths[widthIdx],
      0
    );
  };

  while (totalWidth > containerWidth && shrinkWidthIdx > -1) {
    // shrink left columns to not expand past container width
    shrinkColumn();
    shrinkWidthIdx -= 1;
  }

  return nextWidths;
};

export const adjustColumnWidths = ({
  columnIdx,
  columnIdxMap,
  containerWidth,
  width,
  widths,
}: {
  columnIdx: number;
  columnIdxMap: number[];
  containerWidth: number;
  width: number;
  widths: number[];
}) => {
  const nextWidths = [...widths];
  const widthIdx = columnIdxMap[columnIdx];
  const nextWidthIdx = columnIdxMap[columnIdx + 1];

  if (columnIdx === columnIdxMap.length - 1) {
    // set the last column
    nextWidths[widthIdx] = width;
  } else if (width > nextWidths[widthIdx]) {
    let rightColumnWidth = nextWidths[nextWidthIdx];
    let diff = 0;

    // left col grow, right side col needs to shrink
    diff = width - nextWidths[widthIdx];
    rightColumnWidth -= diff;

    nextWidths[widthIdx] = width;
    nextWidths[nextWidthIdx] = Math.max(MINIMUM_COLUMN_WIDTH, rightColumnWidth);
  } else {
    let rightColumnWidth = nextWidths[nextWidthIdx];
    let diff = 0;

    // ride side col needs to grow
    diff = nextWidths[widthIdx] - width;
    rightColumnWidth += diff;

    nextWidths[widthIdx] = Math.max(MINIMUM_COLUMN_WIDTH, width);
    nextWidths[nextWidthIdx] = rightColumnWidth;
  }

  return fitColumnWidths({
    columnIdxMap,
    containerWidth,
    startWidthIdx: widths.length - 1,
    widths: nextWidths,
  });
};

export const resetColumnWidths = ({
  containerWidth,
  fields,
  widths,
}: {
  containerWidth: number;
  fields: Array<{ hidden?: boolean }>;
  widths?: number[];
}) => {
  // Persisted widths can go stale when the field set shrinks (e.g. deleting a
  // model empties or shortens the table's fields), leaving `widths` longer than
  // `fields`. Rebuild to exactly one width per current field so the maps below
  // never index past `fields`.
  let nextWidths = new Array<number>(fields.length)
    .fill(MINIMUM_COLUMN_WIDTH)
    .map((fallback, fieldIdx) => widths?.[fieldIdx] ?? fallback);

  // Ensure visible columns have at least INITIAL_COLUMN_WIDTH
  nextWidths = nextWidths.map((width, fieldIdx) =>
    !fields[fieldIdx].hidden && width < INITIAL_COLUMN_WIDTH
      ? INITIAL_COLUMN_WIDTH
      : width
  );

  const numColumns = fields.filter((field) => !field.hidden).length;
  if (numColumns === 1) {
    nextWidths = nextWidths.map((width, fieldIdx) =>
      !fields[fieldIdx].hidden ? containerWidth : width
    );
    return nextWidths;
  }

  const fieldWidth = INITIAL_COLUMN_WIDTH;
  const firstWidth = containerWidth - fieldWidth * (numColumns - 1);

  nextWidths = nextWidths.map((width, fieldIdx) =>
    !fields[fieldIdx].hidden
      ? Math.max(INITIAL_COLUMN_WIDTH, fieldIdx ? fieldWidth : firstWidth)
      : width
  );

  const columnIdxMap = nextWidths
    ?.map((_, fieldIdx) => (fields[fieldIdx]?.hidden ? null : fieldIdx))
    .filter((width): width is NonNullable<typeof width> => width !== null);

  return fitColumnWidths({
    containerWidth,
    columnIdxMap,
    startWidthIdx: nextWidths.length - 1,
    widths: nextWidths,
  });
};
